<script setup lang="ts">
import { computed, onMounted, ref } from "vue";

import {
  api,
  type BookingAvailabilityDate,
  type BookingSchedule,
  type BookingSettingsDocument,
  type TableBooking,
} from "./api";

const props = defineProps<{ siteId: string }>();

const weekdays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const calendarWeekdays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const now = new Date();

function isoDate(value: Date) {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function calendarDate(value: string) {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year ?? 0, (month ?? 1) - 1, day ?? 1, 12);
}

function shiftedDate(value: Date, days: number) {
  const shifted = new Date(value);
  shifted.setDate(shifted.getDate() + days);
  return shifted;
}

const today = isoDate(now);
const settings = ref<BookingSettingsDocument | null>(null);
const bookings = ref<TableBooking[]>([]);
const canEdit = ref(false);
const activeView = ref<"bookings" | "availability">("bookings");
const activeLocation = ref("");
const bookingLocation = ref("");
const selectedDate = ref(today);
const calendarMonth = ref(new Date(now.getFullYear(), now.getMonth(), 1, 12));
const availabilityMonth = ref(new Date(now.getFullYear(), now.getMonth(), 1, 12));
const selectedAvailabilityDate = ref("");
const loading = ref(true);
const bookingsLoading = ref(false);
const saving = ref(false);
const changingStatus = ref("");
const error = ref("");
const notice = ref("");
const isAppointment = computed(() => settings.value?.mode === "appointment");

const calendarRange = computed(() => {
  const first = new Date(
    calendarMonth.value.getFullYear(),
    calendarMonth.value.getMonth(),
    1,
    12,
  );
  const mondayOffset = (first.getDay() + 6) % 7;
  const start = shiftedDate(first, -mondayOffset);
  return { start, end: shiftedDate(start, 41) };
});

const bookingsByDate = computed(() => {
  const grouped = new Map<string, TableBooking[]>();
  for (const booking of bookings.value) {
    const day = grouped.get(booking.date) ?? [];
    day.push(booking);
    grouped.set(booking.date, day);
  }
  return grouped;
});

const calendarDays = computed(() =>
  Array.from({ length: 42 }, (_, index) => {
    const value = shiftedDate(calendarRange.value.start, index);
    const date = isoDate(value);
    const dayBookings = bookingsByDate.value.get(date) ?? [];
    const activeBookings = dayBookings.filter(
      (booking) => booking.status !== "declined" && booking.status !== "cancelled",
    );
    return {
      date,
      number: value.getDate(),
      isCurrentMonth: value.getMonth() === calendarMonth.value.getMonth(),
      isToday: date === today,
      bookingCount: dayBookings.length,
      pendingCount: dayBookings.filter((booking) => booking.status === "pending").length,
      covers: activeBookings.reduce((total, booking) => total + booking.partySize, 0),
    };
  }),
);

const monthLabel = computed(() =>
  new Intl.DateTimeFormat("en-GB", { month: "long", year: "numeric" }).format(
    calendarMonth.value,
  ),
);
const availabilityMonthLabel = computed(() =>
  new Intl.DateTimeFormat("en-GB", { month: "long", year: "numeric" }).format(
    availabilityMonth.value,
  ),
);
const availabilityCalendarDays = computed(() => {
  const first = new Date(
    availabilityMonth.value.getFullYear(),
    availabilityMonth.value.getMonth(),
    1,
    12,
  );
  const start = shiftedDate(first, -((first.getDay() + 6) % 7));
  const maximum = settings.value
    ? isoDate(shiftedDate(now, settings.value.maximumAdvanceDays))
    : today;
  return Array.from({ length: 42 }, (_, index) => {
    const value = shiftedDate(start, index);
    const date = isoDate(value);
    const rule = settings.value?.dateAvailability.find(
      (item) => item.locationLabel === activeLocation.value && item.date === date,
    );
    return {
      date,
      number: value.getDate(),
      isCurrentMonth: value.getMonth() === availabilityMonth.value.getMonth(),
      isToday: date === today,
      isDisabled: date < today || date > maximum,
      rule,
    };
  });
});
const selectedAvailability = computed(() =>
  settings.value?.dateAvailability.find(
    (item) =>
      item.locationLabel === activeLocation.value && item.date === selectedAvailabilityDate.value,
  ) ?? null,
);
const selectedDateLabel = computed(() =>
  new Intl.DateTimeFormat("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(calendarDate(selectedDate.value)),
);
const selectedDateBookings = computed(() => bookingsByDate.value.get(selectedDate.value) ?? []);
const visibleSelectedBookings = computed(() =>
  selectedDateBookings.value.filter(
    (booking) => !bookingLocation.value || booking.locationLabel === bookingLocation.value,
  ),
);
const selectedCovers = computed(() =>
  visibleSelectedBookings.value
    .filter((booking) => booking.status !== "declined" && booking.status !== "cancelled")
    .reduce((total, booking) => total + booking.partySize, 0),
);

function newSchedule(): BookingSchedule {
  return {
    locationLabel: activeLocation.value,
    weekday: 0,
    startTime: "12:00",
    endTime: "22:00",
    capacity: isAppointment.value ? 1 : 40,
  };
}

function changeAvailabilityMonth(offset: number) {
  availabilityMonth.value = new Date(
    availabilityMonth.value.getFullYear(),
    availabilityMonth.value.getMonth() + offset,
    1,
    12,
  );
}

function showAvailabilityToday() {
  availabilityMonth.value = new Date(now.getFullYear(), now.getMonth(), 1, 12);
}

function selectAvailabilityDay(day: { date: string; isDisabled: boolean; rule?: BookingAvailabilityDate }) {
  if (!settings.value || day.isDisabled || !activeLocation.value || !canEdit.value) return;
  settings.value.useDateAvailability = true;
  selectedAvailabilityDate.value = day.date;
  if (!day.rule) {
    settings.value.dateAvailability.push({
      locationLabel: activeLocation.value,
      date: day.date,
      allDay: true,
      startTime: "08:00",
      endTime: "20:00",
    });
  }
}

function removeSelectedAvailability() {
  if (!settings.value || !selectedAvailability.value) return;
  const index = settings.value.dateAvailability.indexOf(selectedAvailability.value);
  if (index !== -1) settings.value.dateAvailability.splice(index, 1);
  selectedAvailabilityDate.value = "";
}

function removeSchedule(schedule: BookingSchedule) {
  if (!settings.value) return;
  const index = settings.value.schedules.indexOf(schedule);
  if (index !== -1) settings.value.schedules.splice(index, 1);
}

async function loadBookings() {
  bookingsLoading.value = true;
  error.value = "";
  try {
    bookings.value = (
      await api.bookingsForRange(
        props.siteId,
        isoDate(calendarRange.value.start),
        isoDate(calendarRange.value.end),
      )
    ).bookings;
  } catch (caught) {
    error.value = caught instanceof Error ? caught.message : "Unable to load bookings";
  } finally {
    bookingsLoading.value = false;
  }
}

async function changeMonth(offset: number) {
  calendarMonth.value = new Date(
    calendarMonth.value.getFullYear(),
    calendarMonth.value.getMonth() + offset,
    1,
    12,
  );
  selectedDate.value = isoDate(calendarMonth.value);
  await loadBookings();
}

async function showToday() {
  calendarMonth.value = new Date(now.getFullYear(), now.getMonth(), 1, 12);
  selectedDate.value = today;
  await loadBookings();
}

async function selectDateFromPicker() {
  if (!selectedDate.value) {
    await showToday();
    return;
  }
  const selected = calendarDate(selectedDate.value);
  calendarMonth.value = new Date(selected.getFullYear(), selected.getMonth(), 1, 12);
  await loadBookings();
}

async function selectCalendarDay(day: { date: string; isCurrentMonth: boolean }) {
  selectedDate.value = day.date;
  if (!day.isCurrentMonth) {
    const selected = calendarDate(day.date);
    calendarMonth.value = new Date(selected.getFullYear(), selected.getMonth(), 1, 12);
    await loadBookings();
  }
}

async function saveSettings() {
  if (!settings.value || !canEdit.value) return;
  saving.value = true;
  error.value = "";
  notice.value = "";
  try {
    settings.value = (
      await api.updateBookingSettings(props.siteId, settings.value)
    ).settings;
    notice.value = "Booking availability and settings saved.";
  } catch (caught) {
    error.value = caught instanceof Error ? caught.message : "Unable to save booking settings";
  } finally {
    saving.value = false;
  }
}

async function setStatus(booking: TableBooking, status: TableBooking["status"]) {
  if (!canEdit.value) return;
  changingStatus.value = booking.id;
  error.value = "";
  notice.value = "";
  try {
    const updated = await api.updateBookingStatus(props.siteId, booking.id, status);
    Object.assign(booking, updated.booking);
    notice.value = `Booking ${status}. The customer notification has been queued.`;
  } catch (caught) {
    error.value = caught instanceof Error ? caught.message : "Unable to update the booking";
  } finally {
    changingStatus.value = "";
  }
}

onMounted(async () => {
  try {
    const [settingsResponse] = await Promise.all([
      api.bookingSettings(props.siteId),
      loadBookings(),
    ]);
    settings.value = settingsResponse.settings;
    activeLocation.value = settings.value.locations[0] ?? "";
    canEdit.value = settingsResponse.canEdit;
  } catch (caught) {
    error.value = caught instanceof Error ? caught.message : "Unable to load bookings";
  } finally {
    loading.value = false;
  }
});
</script>

<template>
  <section class="bookings-card">
    <div class="module-heading">
      <div>
        <p class="eyebrow">{{ isAppointment ? "Appointment requests" : "Table bookings" }}</p>
        <h2>{{ isAppointment ? "Appointments and working hours" : "Reservations and availability" }}</h2>
        <p>{{ isAppointment ? "Publish normal working hours and confirm every customer request yourself." : "Manage booking requests and control how many covers can book online." }}</p>
      </div>
      <div class="view-tabs" role="tablist" aria-label="Booking manager">
        <button
          type="button"
          role="tab"
          :aria-selected="activeView === 'bookings'"
          :class="{ active: activeView === 'bookings' }"
          @click="activeView = 'bookings'"
        >
          Bookings
        </button>
        <button
          type="button"
          role="tab"
          :aria-selected="activeView === 'availability'"
          :class="{ active: activeView === 'availability' }"
          @click="activeView = 'availability'"
        >
          Availability &amp; settings
        </button>
      </div>
    </div>

    <p v-if="error" class="message error" role="alert">{{ error }}</p>
    <p v-if="notice" class="message success" role="status">{{ notice }}</p>
    <p v-if="!canEdit && !loading" class="message info">You have read-only access.</p>
    <div v-if="loading" class="loading">Loading bookings…</div>

    <template v-else-if="settings">
      <div v-show="activeView === 'bookings'" class="view-panel">
        <div class="calendar-layout">
          <section class="calendar-panel" aria-label="Booking calendar">
            <div class="calendar-toolbar">
              <div class="calendar-navigation">
                <button class="calendar-arrow" type="button" aria-label="Previous month" @click="changeMonth(-1)">&larr;</button>
                <h3>{{ monthLabel }}</h3>
                <button class="calendar-arrow" type="button" aria-label="Next month" @click="changeMonth(1)">&rarr;</button>
              </div>
              <button class="today-button" type="button" @click="showToday">Today</button>
            </div>

            <div class="calendar-weekdays" aria-hidden="true">
              <span v-for="day in calendarWeekdays" :key="day">{{ day }}</span>
            </div>
            <div class="calendar-grid" :class="{ refreshing: bookingsLoading }">
              <button
                v-for="day in calendarDays"
                :key="day.date"
                class="calendar-day"
                :class="{
                  selected: selectedDate === day.date,
                  today: day.isToday,
                  muted: !day.isCurrentMonth,
                  'has-bookings': day.bookingCount,
                  'has-pending': day.pendingCount,
                }"
                type="button"
                :aria-label="`${day.date}: ${day.bookingCount} bookings`"
                :aria-pressed="selectedDate === day.date"
                @click="selectCalendarDay(day)"
              >
                <span class="day-number">{{ day.number }}</span>
                <span v-if="day.bookingCount" class="day-bookings">
                  <strong>{{ day.bookingCount }}</strong>
                  <small>{{ day.bookingCount === 1 ? "booking" : "bookings" }}</small>
                </span>
                <span v-if="day.bookingCount && !isAppointment" class="day-covers">{{ day.covers }} covers</span>
                <span v-if="day.pendingCount" class="pending-dot" :title="`${day.pendingCount} pending`"></span>
              </button>
            </div>

            <div class="calendar-legend">
              <span><i class="legend-bookings"></i> Bookings</span>
              <span><i class="legend-pending"></i> Awaiting action</span>
            </div>
          </section>

          <section class="day-agenda" aria-live="polite">
            <div class="agenda-heading">
              <div>
                <p class="eyebrow">Selected day</p>
                <h3>{{ selectedDateLabel }}</h3>
                <p>{{ visibleSelectedBookings.length }} {{ isAppointment ? "requests" : "bookings" }}<template v-if="!isAppointment"> &middot; {{ selectedCovers }} active covers</template></p>
              </div>
              <input
                v-model="selectedDate"
                class="agenda-date-picker"
                type="date"
                aria-label="Choose booking date"
                @change="selectDateFromPicker"
              >
            </div>

            <div v-if="settings.locations.length > 1" class="booking-location-tabs" aria-label="Filter bookings by location">
              <button type="button" :class="{ active: !bookingLocation }" @click="bookingLocation = ''">
                All <span>{{ selectedDateBookings.length }}</span>
              </button>
              <button
                v-for="location in settings.locations"
                :key="location"
                type="button"
                :class="{ active: bookingLocation === location }"
                @click="bookingLocation = location"
              >
                {{ location }}
                <span>{{ selectedDateBookings.filter((booking) => booking.locationLabel === location).length }}</span>
              </button>
            </div>

            <div v-if="bookingsLoading" class="agenda-loading">Loading bookings...</div>
            <div v-else-if="visibleSelectedBookings.length" class="booking-list">
              <article v-for="booking in visibleSelectedBookings" :key="booking.id" class="booking-row">
                <div class="booking-time">
                  <strong>{{ booking.time }}</strong>
                  <span>{{ booking.partySize }} {{ isAppointment ? (booking.partySize === 1 ? "cat" : "cats") : (booking.partySize === 1 ? "person" : "people") }}</span>
                </div>
                <div class="booking-customer">
                  <div class="booking-name-line">
                    <strong>{{ booking.customerName }}</strong>
                    <span class="status" :class="booking.status">{{ booking.status }}</span>
                  </div>
                  <span>{{ booking.locationLabel }}</span>
                  <small>{{ booking.telephone }} &middot; {{ booking.email }}</small>
                  <small v-if="booking.notes" class="booking-notes">{{ booking.notes }}</small>
                </div>
                <div class="booking-actions">
                  <button
                    v-if="booking.status !== 'confirmed'"
                    type="button"
                    :disabled="!canEdit || changingStatus === booking.id"
                    @click="setStatus(booking, 'confirmed')"
                  >
                    Confirm
                  </button>
                  <button
                    v-if="booking.status !== 'declined'"
                    class="danger"
                    type="button"
                    :disabled="!canEdit || changingStatus === booking.id"
                    @click="setStatus(booking, 'declined')"
                  >
                    Decline
                  </button>
                </div>
              </article>
            </div>
            <div v-else class="agenda-empty">
              <span class="empty-calendar-icon">{{ calendarDate(selectedDate).getDate() }}</span>
              <strong>No {{ isAppointment ? "requests" : "bookings" }} for this day</strong>
              <p>Select a highlighted date in the calendar to view its {{ isAppointment ? "appointment requests" : "reservations" }}.</p>
            </div>
          </section>
        </div>
      </div>

      <div v-show="activeView === 'availability'" class="view-panel">
        <section class="settings-panel">
          <div class="settings-grid">
            <label>
              Notification email
              <input v-model="settings.notificationEmail" :disabled="!canEdit" type="email" placeholder="bookings@example.com">
            </label>
            <label>
              {{ isAppointment ? "Maximum cats per request" : "Maximum party size" }}
              <input v-model.number="settings.maximumPartySize" :disabled="!canEdit" type="number" min="1" max="100">
            </label>
            <label>
              Slot interval
              <select v-model.number="settings.slotIntervalMinutes" :disabled="!canEdit">
                <option :value="15">15 minutes</option>
                <option :value="30">30 minutes</option>
                <option :value="45">45 minutes</option>
                <option :value="60">60 minutes</option>
              </select>
            </label>
            <label>
              Typical {{ isAppointment ? "visit" : "booking" }} duration
              <input v-model.number="settings.defaultDurationMinutes" :disabled="!canEdit" type="number" min="15" max="720" step="15">
            </label>
            <label>
              Minimum notice (hours)
              <input v-model.number="settings.minimumNoticeHours" :disabled="!canEdit" type="number" min="0" max="720">
            </label>
            <label>
              Book up to (days ahead)
              <input v-model.number="settings.maximumAdvanceDays" :disabled="!canEdit" type="number" min="1" max="730">
            </label>
          </div>
          <label v-if="!isAppointment" class="check-row">
            <input v-model="settings.autoConfirm" :disabled="!canEdit" type="checkbox">
            Automatically confirm bookings with available capacity
          </label>
        </section>

        <section v-if="isAppointment" class="availability-date-panel">
          <div class="section-heading">
            <div>
              <h3>Working-day calendar</h3>
              <p>Click a date to make it available, then choose all day or set specific hours.</p>
            </div>
            <label class="check-row calendar-mode-toggle">
              <input v-model="settings.useDateAvailability" :disabled="!canEdit" type="checkbox">
              Use selected calendar dates for bookings
            </label>
          </div>

          <p v-if="!settings.locations.length" class="message info">
            Add at least one published service in Services &amp; prices before choosing working days.
          </p>
          <template v-else>
            <div class="location-tabs" role="tablist" aria-label="Calendar availability service">
              <button
                v-for="location in settings.locations"
                :key="location"
                type="button"
                role="tab"
                :aria-selected="activeLocation === location"
                :class="{ active: activeLocation === location }"
                @click="activeLocation = location; selectedAvailabilityDate = ''"
              >
                {{ location }}
                <span>{{ settings.dateAvailability.filter((item) => item.locationLabel === location).length }}</span>
              </button>
            </div>

            <div class="availability-calendar-layout">
              <div class="availability-calendar">
                <div class="calendar-toolbar">
                  <div class="calendar-navigation">
                    <button class="calendar-arrow" type="button" aria-label="Previous month" @click="changeAvailabilityMonth(-1)">&larr;</button>
                    <h3>{{ availabilityMonthLabel }}</h3>
                    <button class="calendar-arrow" type="button" aria-label="Next month" @click="changeAvailabilityMonth(1)">&rarr;</button>
                  </div>
                  <button class="today-button" type="button" @click="showAvailabilityToday">Today</button>
                </div>
                <div class="calendar-weekdays" aria-hidden="true">
                  <span v-for="day in calendarWeekdays" :key="day">{{ day }}</span>
                </div>
                <div class="availability-grid">
                  <button
                    v-for="day in availabilityCalendarDays"
                    :key="day.date"
                    class="availability-day"
                    :class="{
                      available: day.rule,
                      selected: selectedAvailabilityDate === day.date,
                      today: day.isToday,
                      muted: !day.isCurrentMonth,
                    }"
                    type="button"
                    :disabled="day.isDisabled || !canEdit"
                    :aria-pressed="Boolean(day.rule)"
                    @click="selectAvailabilityDay(day)"
                  >
                    <span>{{ day.number }}</span>
                    <small v-if="day.rule">{{ day.rule.allDay ? "All day" : `${day.rule.startTime}–${day.rule.endTime}` }}</small>
                  </button>
                </div>
                <div class="availability-legend"><span><i></i> Available to request</span><span>All day means 8:00am–8:00pm.</span></div>
              </div>

              <aside class="availability-editor">
                <template v-if="selectedAvailability">
                  <div>
                    <p class="eyebrow">Selected working day</p>
                    <h3>{{ new Intl.DateTimeFormat('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).format(calendarDate(selectedAvailability.date)) }}</h3>
                    <p>{{ activeLocation }}</p>
                  </div>
                  <label class="all-day-option">
                    <input v-model="selectedAvailability.allDay" :disabled="!canEdit" type="checkbox">
                    <span><strong>All day</strong><small>Offer appointment times from 8:00am to 8:00pm.</small></span>
                  </label>
                  <div v-if="!selectedAvailability.allDay" class="availability-times">
                    <label>From<input v-model="selectedAvailability.startTime" :disabled="!canEdit" type="time"></label>
                    <label>Until<input v-model="selectedAvailability.endTime" :disabled="!canEdit" type="time"></label>
                  </div>
                  <button class="danger" type="button" :disabled="!canEdit" @click="removeSelectedAvailability">Remove this working day</button>
                </template>
                <div v-else class="availability-help">
                  <span aria-hidden="true">✓</span>
                  <strong>Choose a working day</strong>
                  <p>Click any future date in the calendar. Green dates are available to customers after you save.</p>
                </div>
              </aside>
            </div>
          </template>
        </section>

        <section class="schedule-panel">
          <div class="section-heading">
            <div>
              <h3>{{ isAppointment ? "Weekly fallback availability" : "Weekly availability" }}</h3>
              <p>{{ isAppointment ? "Used only when the working-day calendar above is switched off." : "Capacity is the maximum number of simultaneous covers during this service." }}</p>
            </div>
            <button class="secondary" type="button" :disabled="!canEdit || !settings.locations.length" @click="settings.schedules.push(newSchedule())">
              Add availability<span v-if="activeLocation"> for {{ activeLocation }}</span>
            </button>
          </div>
          <p v-if="!settings.locations.length" class="message info">
            {{ isAppointment ? "Add at least one published service in Services & prices before configuring appointments." : "Add a location in the Locations tab before configuring bookings." }}
          </p>
          <div v-else class="location-tabs" role="tablist" aria-label="Availability location">
            <button
              v-for="location in settings.locations"
              :key="location"
              type="button"
              role="tab"
              :aria-selected="activeLocation === location"
              :class="{ active: activeLocation === location }"
              @click="activeLocation = location"
            >
              {{ location }}
              <span>{{ settings.schedules.filter((item) => item.locationLabel === location).length }}</span>
            </button>
          </div>
          <div
            v-for="(schedule, index) in settings.schedules"
            v-show="schedule.locationLabel === activeLocation"
            :key="schedule.id || index"
            class="schedule-row"
          >
            <label>Day<select v-model.number="schedule.weekday" :disabled="!canEdit"><option v-for="(day, dayIndex) in weekdays" :key="day" :value="dayIndex">{{ day }}</option></select></label>
            <label>From<input v-model="schedule.startTime" :disabled="!canEdit" type="time"></label>
            <label>Until<input v-model="schedule.endTime" :disabled="!canEdit" type="time"></label>
            <label v-if="!isAppointment">Capacity<input v-model.number="schedule.capacity" :disabled="!canEdit" type="number" min="1" max="500"></label>
            <button class="danger remove-schedule" type="button" :disabled="!canEdit" @click="removeSchedule(schedule)">Remove</button>
          </div>
          <p
            v-if="settings.locations.length && !settings.schedules.some((item) => item.locationLabel === activeLocation)"
            class="empty"
          >
            No weekly availability has been added for {{ activeLocation }}.
          </p>
        </section>

        <div class="module-save">
          <span>{{ settings.schedules.length + settings.dateAvailability.length }} availability rules</span>
          <button type="button" :disabled="saving || !canEdit" @click="saveSettings">
            {{ saving ? "Saving…" : "Save booking settings" }}
          </button>
        </div>
      </div>
    </template>
  </section>
</template>

<style scoped>
.bookings-card { display: grid; gap: 1.25rem; padding: 1.5rem; border: 1px solid #d9ddd8; border-radius: 1rem; background: white; }
.module-heading, .section-heading, .module-save { display: flex; align-items: center; justify-content: space-between; gap: 1rem; }
.module-heading h2, .section-heading h3 { margin: 0; }
.module-heading p:last-child, .section-heading p { margin: .35rem 0 0; color: #6d7972; }
.eyebrow { margin: 0 0 .25rem; color: #6b7b72; font-size: .68rem; font-weight: 800; letter-spacing: .1em; text-transform: uppercase; }
.view-tabs { display: flex; padding: .25rem; border-radius: .65rem; background: #edf1ed; }
.view-tabs button { color: #52645a; background: transparent; }
.view-tabs button.active { color: white; background: #285c47; }
.view-panel { display: grid; gap: 1rem; }
.calendar-layout { display: grid; align-items: start; gap: 1rem; grid-template-columns: minmax(32rem, 1.35fr) minmax(24rem, 1fr); }
.calendar-panel, .day-agenda { overflow: hidden; border: 1px solid #dce2dc; border-radius: .85rem; background: #fafbf9; }
.calendar-toolbar { display: flex; min-height: 4.3rem; padding: .8rem 1rem; align-items: center; justify-content: space-between; gap: 1rem; border-bottom: 1px solid #dfe5df; background: white; }
.calendar-navigation { display: flex; align-items: center; gap: .55rem; }
.calendar-navigation h3 { width: 10.5rem; margin: 0; font-size: 1.05rem; text-align: center; }
.calendar-arrow, .today-button { border: 1px solid #d3ddd6; color: #315044; background: white; }
.calendar-arrow { display: grid; width: 2.25rem; height: 2.25rem; padding: 0; place-items: center; }
.today-button { padding: .5rem .75rem; }
.calendar-weekdays, .calendar-grid { display: grid; grid-template-columns: repeat(7, minmax(0, 1fr)); }
.calendar-weekdays { padding: .45rem .65rem; color: #728078; background: #f1f4f1; font-size: .64rem; font-weight: 800; letter-spacing: .08em; text-align: center; text-transform: uppercase; }
.calendar-grid { border-top: 1px solid #e1e6e2; border-left: 1px solid #e1e6e2; transition: opacity .15s; }
.calendar-grid.refreshing { opacity: .55; pointer-events: none; }
.calendar-day { display: grid; position: relative; min-width: 0; min-height: 6rem; padding: .5rem; align-content: start; gap: .2rem; border-right: 1px solid #e1e6e2; border-bottom: 1px solid #e1e6e2; border-radius: 0; color: #31463d; background: white; text-align: left; }
.calendar-day:hover { z-index: 1; background: #f0f5f1; box-shadow: inset 0 0 0 2px #91aa9e; }
.calendar-day.muted { color: #99a39d; background: #f7f8f6; }
.calendar-day.selected { z-index: 2; color: white; background: #285c47; box-shadow: inset 0 0 0 2px #1e4938; }
.calendar-day.today .day-number { border: 1px solid #d4633a; color: #a54726; }
.calendar-day.selected.today .day-number { border-color: #ffc3a9; color: white; }
.day-number { display: grid; width: 1.55rem; height: 1.55rem; place-items: center; border-radius: 50%; font-size: .72rem; font-weight: 800; }
.day-bookings { display: flex; min-width: 0; align-items: baseline; gap: .25rem; color: #285c47; }
.day-bookings strong { font-size: 1rem; }
.day-bookings small, .day-covers { overflow: hidden; font-size: .6rem; text-overflow: ellipsis; white-space: nowrap; }
.day-covers { color: #748178; }
.calendar-day.selected .day-bookings, .calendar-day.selected .day-covers { color: white; }
.pending-dot { position: absolute; top: .55rem; right: .55rem; width: .48rem; height: .48rem; border: 2px solid white; border-radius: 50%; background: #d4633a; box-shadow: 0 0 0 1px #d4633a; }
.calendar-day.selected .pending-dot { background: #ffc39f; box-shadow: 0 0 0 1px #ffc39f; }
.calendar-legend { display: flex; padding: .65rem 1rem; gap: 1rem; color: #6d7972; background: white; font-size: .67rem; }
.calendar-legend span { display: inline-flex; align-items: center; gap: .35rem; }
.calendar-legend i { display: block; width: .5rem; height: .5rem; border-radius: 50%; }
.legend-bookings { background: #285c47; }
.legend-pending { background: #d4633a; }
.day-agenda { display: grid; min-width: 0; }
.agenda-heading { display: flex; min-height: 6.8rem; padding: 1rem; align-items: flex-start; justify-content: space-between; gap: .8rem; border-bottom: 1px solid #dfe5df; background: white; }
.agenda-heading h3 { margin: 0; font-size: 1.08rem; }
.agenda-heading p:last-child { margin: .35rem 0 0; color: #718078; font-size: .75rem; }
.agenda-date-picker { width: 2.8rem; min-width: 2.8rem; padding: .55rem; color: transparent; }
.agenda-date-picker::-webkit-calendar-picker-indicator { width: 100%; height: 100%; margin: 0; cursor: pointer; }
.booking-location-tabs { display: flex; overflow-x: auto; padding: .55rem; gap: .3rem; border-bottom: 1px solid #dfe5df; background: #eef2ee; }
.booking-location-tabs button { display: inline-flex; min-width: max-content; padding: .45rem .6rem; align-items: center; gap: .35rem; color: #52645a; background: transparent; font-size: .7rem; }
.booking-location-tabs button.active { color: white; background: #285c47; }
.booking-location-tabs span { min-width: 1.2rem; padding: .08rem .3rem; border-radius: 999px; color: #52645a; background: white; font-size: .62rem; text-align: center; }
.booking-list { display: grid; max-height: 35rem; overflow-y: auto; padding: .7rem; gap: .55rem; scrollbar-width: auto; scrollbar-color: #789286 #edf1ed; }
.booking-row { display: grid; padding: .85rem; align-items: start; gap: .75rem; grid-template-columns: 4.2rem minmax(0, 1fr); border: 1px solid #dce2dc; border-radius: .72rem; background: white; }
.booking-time, .booking-customer { display: grid; min-width: 0; gap: .2rem; }
.booking-time strong { color: #285c47; font-size: 1.05rem; }
.booking-time span, .booking-customer > span, .booking-customer small { color: #65746c; font-size: .72rem; }
.booking-name-line { display: flex; min-width: 0; align-items: center; justify-content: space-between; gap: .5rem; }
.booking-name-line strong { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.booking-notes { margin-top: .3rem; padding: .45rem; border-radius: .4rem; background: #f1f4f1; line-height: 1.45; }
.status { padding: .35rem .55rem; border-radius: 999px; font-size: .7rem; font-weight: 800; text-transform: capitalize; }
.status.pending { color: #805824; background: #faedd9; }
.status.confirmed { color: #285a3e; background: #dff0e4; }
.status.declined, .status.cancelled { color: #8d3730; background: #fae9e7; }
.booking-actions { display: flex; grid-column: 2; gap: .4rem; }
.booking-actions button { padding: .5rem .7rem; }
.agenda-loading, .agenda-empty { display: grid; min-height: 20rem; padding: 2rem; place-content: center; justify-items: center; color: #758078; text-align: center; }
.agenda-empty { gap: .5rem; }
.agenda-empty p { max-width: 19rem; margin: 0; font-size: .78rem; line-height: 1.5; }
.empty-calendar-icon { display: grid; width: 3rem; height: 3rem; margin-bottom: .4rem; place-items: center; border: 1px solid #cbd5ce; border-radius: .7rem; color: #285c47; background: white; font-weight: 800; box-shadow: inset 0 .45rem 0 #d4633a; }
.danger { color: #91382f; background: #fae9e7; }
.secondary { border: 1px solid #c8d2cb; color: #29483a; background: white; }
.settings-panel, .schedule-panel, .availability-date-panel { display: grid; gap: 1rem; padding: 1.2rem; border: 1px solid #dce2dc; border-radius: .8rem; background: #fafbf9; }
.settings-grid { display: grid; gap: 1rem; grid-template-columns: repeat(2, 1fr); }
label { display: grid; gap: .4rem; }
select { width: 100%; padding: .75rem; border: 1px solid #cbd5ce; border-radius: .65rem; background: white; font: inherit; }
.check-row { display: flex; align-items: center; gap: .55rem; }
.check-row input { width: auto; }
.calendar-mode-toggle { padding: .7rem .85rem; border-radius: .65rem; background: #e8efe9; font-size: .78rem; font-weight: 800; }
.availability-calendar-layout { display: grid; grid-template-columns: minmax(32rem, 1.35fr) minmax(19rem, .65fr); gap: 1rem; align-items: stretch; }
.availability-calendar, .availability-editor { overflow: hidden; border: 1px solid #dce2dc; border-radius: .85rem; background: white; }
.availability-grid { display: grid; grid-template-columns: repeat(7, minmax(0, 1fr)); border-top: 1px solid #e1e6e2; border-left: 1px solid #e1e6e2; }
.availability-day { display: grid; min-width: 0; min-height: 5rem; padding: .45rem; align-content: start; gap: .35rem; border-right: 1px solid #e1e6e2; border-bottom: 1px solid #e1e6e2; border-radius: 0; color: #31463d; background: white; text-align: left; }
.availability-day:hover:not(:disabled) { z-index: 1; background: #eef5f0; box-shadow: inset 0 0 0 2px #8caf9d; }
.availability-day>span { display: grid; width: 1.55rem; height: 1.55rem; place-items: center; border-radius: 50%; font-size: .72rem; font-weight: 800; }
.availability-day small { overflow: hidden; color: inherit; font-size: .59rem; font-weight: 800; text-overflow: ellipsis; white-space: nowrap; }
.availability-day.muted { color: #99a39d; background: #f7f8f6; }
.availability-day.today>span { border: 1px solid #d4633a; color: #a54726; }
.availability-day.available { color: white; background: #478465; box-shadow: inset 0 .32rem 0 #285c47; }
.availability-day.selected { z-index: 2; box-shadow: inset 0 0 0 3px #e89171, inset 0 .32rem 0 #e89171; }
.availability-day:disabled { cursor: not-allowed; opacity: .38; }
.availability-legend { display: flex; min-height: 2.8rem; padding: .65rem 1rem; align-items: center; justify-content: space-between; gap: 1rem; color: #6d7972; background: white; font-size: .67rem; }
.availability-legend span { display: inline-flex; align-items: center; gap: .35rem; }.availability-legend i { width: .55rem; height: .55rem; border-radius: 50%; background: #478465; }
.availability-editor { display: grid; padding: 1.2rem; align-content: start; gap: 1.2rem; background: #f4f7f4; }
.availability-editor h3 { margin: 0; font-size: 1.15rem; }.availability-editor>div>p:last-child { margin: .35rem 0 0; color: #6d7972; font-size: .76rem; }
.all-day-option { display: flex; padding: 1rem; align-items: flex-start; gap: .75rem; border: 1px solid #cbd8cf; border-radius: .75rem; background: white; }.all-day-option input { width: auto; margin-top: .2rem; }.all-day-option strong,.all-day-option small { display: block; }.all-day-option small { margin-top: .25rem; color: #6d7972; font-size: .7rem; line-height: 1.45; }
.availability-times { display: grid; grid-template-columns: 1fr 1fr; gap: .7rem; }.availability-help { display: grid; min-height: 19rem; place-content: center; justify-items: center; color: #6d7972; text-align: center; }.availability-help>span { display: grid; width: 3rem; height: 3rem; margin-bottom: .8rem; place-items: center; border-radius: 50%; color: white; background: #478465; font-weight: 900; }.availability-help p { max-width: 17rem; line-height: 1.5; }
.location-tabs { display: flex; overflow-x: auto; padding: .25rem; gap: .25rem; border-radius: .7rem; background: #e9eeea; }
.location-tabs button { display: inline-flex; min-width: max-content; align-items: center; gap: .5rem; color: #52645a; background: transparent; }
.location-tabs button.active { color: white; background: #285c47; }
.location-tabs span { min-width: 1.35rem; padding: .1rem .35rem; border-radius: 999px; color: #52645a; background: white; font-size: .68rem; }
.schedule-row { display: grid; align-items: end; gap: .65rem; grid-template-columns: 1.3fr 1fr 1fr .8fr auto; }
.remove-schedule { min-height: 2.7rem; }
.module-save { padding-top: 1rem; border-top: 1px solid #e1e5e1; color: #6d7972; font-size: .8rem; }
.message { margin: 0; padding: .85rem 1rem; border-radius: .65rem; }
.message.error { color: #86251f; background: #fee9e7; }
.message.success { color: #295b40; background: #e2f1e7; }
.message.info { color: #4c596e; background: #e8edf7; }
.loading, .empty { padding: 2rem; color: #758078; text-align: center; }
@media (max-width: 1100px) {
  .module-heading, .section-heading, .module-save { align-items: stretch; flex-direction: column; }
  .calendar-layout { grid-template-columns: 1fr; }
  .availability-calendar-layout { grid-template-columns: 1fr; }
  .schedule-row { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 600px) {
  .settings-grid, .schedule-row, .availability-times { grid-template-columns: 1fr; }
  .view-tabs { width: 100%; overflow-x: auto; }
  .view-tabs button { min-width: max-content; }
  .calendar-toolbar { align-items: stretch; flex-direction: column; }
  .calendar-navigation { justify-content: space-between; }
  .today-button { width: 100%; }
  .calendar-day { min-height: 4.4rem; padding: .3rem; }
  .availability-day { min-height: 4rem; padding: .3rem; }
  .availability-day small { display: none; }
  .day-bookings small, .day-covers { display: none; }
  .day-bookings strong { font-size: .75rem; }
  .pending-dot { top: .35rem; right: .35rem; }
  .agenda-heading { align-items: flex-start; }
  .booking-row { grid-template-columns: 3.8rem minmax(0, 1fr); }
}
</style>
